<template>
  <v-menu
    v-model="isVisible"
    :position-x="x"
    :position-y="y"
    absolute
  >
    <cancel-reservation-dialog
      v-if="confirmCancelReservation"
      :reservation="reservation"
      @refund-reservation="cancelReservation(true)"
      @cancel-reservation="cancelReservation"
      @close="confirmCancelReservation=false"
    />
    <v-list class="py-0">
      <v-list-item
        v-for="status in items"
        :key="status.value"
        @click="setReservationStatus(status.value)"
      >
        <v-list-item-content>
          <v-list-item-title>
            {{ $t('label.status_set_to') }}
            <v-icon
              x-small
              class="ml-1 mr-1"
              style="padding-bottom: 3px"
              :color="colorForStatus(status)"
            >
              mdi-circle
            </v-icon>
            <b>{{ status.text }}</b>
          </v-list-item-title>
          <v-list-item-subtitle v-if="isCheckedOutStatus(status.value)">
            {{ endDateTextForCheckedOut }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
      <v-divider />
      <v-list-item @click="editReservationClicked">
        <v-list-item-title>{{ $t('label.edit_reservation') }}</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script lang="ts">
import { cloneModel } from '@/model/model-utils';
import Reservation, { ReservationStatus } from '@/model/Reservation';
import { tryEditReservation } from '@/services/reservation-edit-utils';
import { endDateForCheckedOut } from '@/services/reservation-utils';
import { performSaveAction } from '@/services/vue-utils';
import { nextStatusItems, nextStatusItemsBlock } from '@/util/status';
import Vue, { PropType } from 'vue';
import { mapActions } from 'vuex';
import { colorForStatus } from '@/components/helpers/colors';
import CancelReservationDialog from '@/components/dialogs/CancelReservationDialog.vue';

export default Vue.extend({
  name: 'StatusContextMenu',
  components: { CancelReservationDialog },
  props: {
    visible: { type: Boolean, required: true },
    x: { type: Number, required: true },
    y: { type: Number, required: true },
    reservation: { type: Object as PropType<Reservation>, required: true },
  },
  data() {
    return {
      isVisible: this.visible,
      confirmCancelReservation: false,
    };
  },
  computed: {
    items() {
      if (!this.reservation) return [];
      return this.reservation.isBlock
        ? nextStatusItemsBlock(this.reservation.status)
        : nextStatusItems(this.reservation.status);
    },
    endDateTextForCheckedOut(): string | null {
      if (!this.reservation) return null;

      const endDate = endDateForCheckedOut(this.reservation);
      const endDateText = this.$localized.hourMinText(endDate);
      if (!endDateText) return null;
      const text = this.$i18n.t('label.ending_time_will_be_x', [endDateText]).toString();
      return text;
    },
  },
  watch: {
    visible() {
      this.isVisible = this.visible;
    },
    isVisible() {
      if (this.isVisible === false) {
        this.$emit('close');
      }
    },
  },
  mounted() { },
  methods: {
    ...mapActions(['editReservation', 'sendReservation', 'setReservationFromContextStatusChange']),
    async setReservationStatus(status: ReservationStatus) {
      if (!this.reservation) { return; }

      if (status === ReservationStatus.Cancelled) {
        this.confirmCancelReservation = true;
        return;
      }

      const r = cloneModel(this.reservation);
      r.setStatus(status as ReservationStatus);
      const ok = await performSaveAction(
        undefined,
        async () => this.sendReservation({ reservation: r }),
      );
      if (ok) this.setReservationFromContextStatusChange(r);
    },
    async cancelReservation(requestRefund?: boolean) {
      const r = cloneModel(this.reservation);
      r.setStatus(ReservationStatus.Cancelled);
      r.isRefundRequested = requestRefund ?? null;
      const ok = await performSaveAction(
        undefined,
        async () => this.sendReservation({ reservation: r }),
      );
      if (ok) this.setReservationFromContextStatusChange(r);
    },
    async editReservationClicked() {
      if (!this.reservation) { return; }
      await tryEditReservation(this.$router, this.reservation);
    },
    isCheckedOutStatus(s: ReservationStatus): boolean {
      return s === ReservationStatus.CheckedOut;
    },
    colorForStatus(s: any): string {
      return colorForStatus(s.value);
    },
  },
});

</script>
